
import { createClient } from '@supabase/supabase-js'
import { Capacitor } from '@capacitor/core'
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth'
import { SignInWithApple } from '@capacitor-community/apple-sign-in'

const supabaseUrl = process.env.VUE_APP_SUPABASE_URL
const supabaseAnonKey = process.env.VUE_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Supabase URL or Anon Key is missing! Set VUE_APP_SUPABASE_URL and VUE_APP_SUPABASE_KEY in .env')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        // If running on native, persist session differently?
        // Supabase JS auto-handles session persistence with localStorage/AsyncStorage
        // For Capacitor, we might need a custom storage adapter if we want native persistence,
        // but the user said "no storage in device".
        // So session persistence: true/false?
        // "no storage in device" usually refers to game data. Auth token is usually okay for UX.
        // However, "webapp version: no data is stored... when refresh, all changes are gone".
        // This implies ephemeral session for web.
        // Native app usually stays logged in.
        persistSession: Capacitor.isNativePlatform(),
        autoRefreshToken: Capacitor.isNativePlatform(),
    }
})

const GOOGLE_AUTH_OPTIONS = {
    clientId: '15951223708-up9407mqsp2br78pn8t72l2sao0kkfaj.apps.googleusercontent.com',
    scopes: ['profile', 'email'],
    grantOfflineAccess: true,
}

let googleAuthReady = null
function ensureGoogleAuth() {
    if (!googleAuthReady) {
        googleAuthReady = GoogleAuth.initialize(GOOGLE_AUTH_OPTIONS)
    }
    return googleAuthReady
}

export async function signInWithGoogle() {
    try {
        if (Capacitor.getPlatform() === 'web') {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: window.location.origin
                }
            })
            if (error) throw error
            return data
        } else {
            await ensureGoogleAuth()

            // Force account picker by signing out first
            try {
                await GoogleAuth.signOut();
            } catch (e) {
                // Ignore errors if not currently signed in
            }

            // Native Google Sign In
            const googleUser = await GoogleAuth.signIn()
            const idToken = googleUser.authentication.idToken

            const { data, error } = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token: idToken,
            })
            if (error) throw error
            return data
        }
    } catch (error) {
        console.error('Google Sign In Error:', error)
        throw error
    }
}

export async function signInWithApple() {
    try {
        if (Capacitor.getPlatform() === 'web') {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'apple',
                options: {
                    redirectTo: window.location.origin
                }
            })
            if (error) throw error
            return data
        } else {
            // Native Apple Sign In
            const result = await SignInWithApple.authorize({
                clientId: 'com.kosamng.mahjongtw',
                scopes: 'name email',
            })

            if (result.response && result.response.identityToken) {
                const { data, error } = await supabase.auth.signInWithIdToken({
                    provider: 'apple',
                    token: result.response.identityToken,
                })
                if (error) throw error
                return data
            } else {
                throw new Error('No identity token received from Apple')
            }
        }
    } catch (error) {
        console.error('Apple Sign In Error:', error)
        throw error
    }
}

export async function signOut() {
    const { error } = await supabase.auth.signOut()
    if (Capacitor.getPlatform() !== 'web') {
        try {
            await ensureGoogleAuth()
            await GoogleAuth.signOut()
        } catch (e) {
            // Ignore if not signed in or not initialized
        }
    }
    if (error) throw error
}

/**
 * Deletes the user account and all associated data.
 * Note: Deleting the auth user record requires a Supabase RPC or Service Role.
 * This function clears user-owned data and signs the user out.
 */
export async function deleteAccount() {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    // 1. Delete all game profiles
    const { error: profileError } = await supabase
        .from('game_profiles')
        .delete()
        .eq('user_id', user.id)

    if (profileError) throw profileError

    // 2. Attempt to call an RPC to delete the auth user record
    // This is optional and depends on the backend setup, but we provide the call here.
    // The developer should ensure an RPC named 'delete_user_account' exists.
    try {
        await supabase.rpc('delete_user_account')
    } catch (e) {
        console.warn('RPC delete_user_account not found or failed. This is expected if not set up on Supabase.', e)
    }

    // 3. Sign out
    await signOut()
}

export async function getUser() {
    const { data } = await supabase.auth.getUser()
    return data.user
}

// Profile Management Services

/**
 * Fetches all game profiles for a specific user.
 * @param {string} userId 
 */
export async function fetchGameProfiles(userId) {
    const { data, error } = await supabase
        .from('game_profiles')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })

    if (error) throw error
    return data
}

/**
 * Creates a new game profile in Supabase.
 */
export async function createSupabaseProfile(userId, name, groups) {
    const { data, error } = await supabase
        .from('game_profiles')
        .insert([
            { user_id: userId, name: name, groups: groups, updated_at: new Date() }
        ])
        .select()

    if (error) throw error
    return data[0]
}

/**
 * Updates an existing game profile in Supabase.
 */
export async function updateSupabaseProfile(id, name, groups) {
    const { data, error } = await supabase
        .from('game_profiles')
        .update({ name: name, groups: groups, updated_at: new Date() })
        .eq('id', id)
        .select()

    if (error) throw error
    return data[0]
}

/**
 * Deletes a game profile from Supabase.
 */
export async function deleteSupabaseProfile(id) {
    const { error } = await supabase
        .from('game_profiles')
        .delete()
        .eq('id', id)

    if (error) throw error
    return true
}

/**
 * Compute a SHA-256 hash of a string, returned as a hex string.
 * Used to deduplicate shared profiles by content.
 */
async function hashContent(str) {
    const msgBuffer = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Creates a shared profile with a random UUID token (no user linkage).
 * Reuses an existing token if the same profile content was already shared.
 * @param {string} name - Profile name
 * @param {Array} groups - Profile groups data
 * @returns {string} The share token
 */
export async function createSharedProfile(name, groups) {
    // 1. Compute a stable content hash from the profile data
    const canonical = JSON.stringify({ name, groups });
    const contentHash = await hashContent(canonical);

    // 2. Look up by hash — fast exact match on text column
    const { data: existing } = await supabase
        .from('shared_profiles')
        .select('token')
        .eq('content_hash', contentHash)
        .limit(1)
        .single();

    if (existing) return existing.token;

    // 3. Otherwise create a new entry
    const token = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );

    const { error } = await supabase
        .from('shared_profiles')
        .insert([{ token, name, groups, content_hash: contentHash }]);

    if (error) throw error;
    return token;
}

/**
 * Fetches a shared profile by its token.
 * @param {string} token
 * @returns {{ name: string, groups: Array } | null}
 */
export async function fetchSharedProfile(token) {
    const { data, error } = await supabase
        .from('shared_profiles')
        .select('name, groups, created_at')
        .eq('token', token)
        .single()

    if (error) throw error
    return data
}
